import React, { useEffect, useRef, useState } from "react";
import { DealStage, STAGE_ACTIONS, stageLabel } from "./types";
import { useStageChange } from "./use-stage-change";

// Shared stage-move control used by both the board card and the details
// drawer — PATCH /deals/{id}/stage per docs/deals-ui-integration.md, via the
// use-stage-change.tsx hook (also used by the kanban board's drag-and-drop
// in index.tsx, so both ways of moving a deal go through the exact same
// request and "won"/"lost" extra-info prompts).

const STAGE_STYLES: Record<DealStage, string> = {
  new: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  qualified: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
  proposal: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400",
  negotiation: "bg-purple-50 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400",
  follow_up: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  won: "bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-400",
  lost: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400",
};

const StageMenu: React.FC<{
  dealId: string;
  stage: DealStage;
  onChanged: (stage: DealStage) => void;
  className?: string;
}> = ({ dealId, stage, onChanged, className }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { requestStageChange, modals, submitting } = useStageChange((_dealId, nextStage) => onChanged(nextStage));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePick = (nextStage: DealStage) => {
    setOpen(false);
    requestStageChange(dealId, stage, nextStage);
  };

  return (
    <div className={`relative inline-block ${className || ""}`} ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        disabled={submitting}
        className={`text-xs font-medium px-2.5 py-1 rounded-full disabled:opacity-60 ${STAGE_STYLES[stage] || STAGE_STYLES.new}`}
      >
        {stageLabel(stage)}
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-36 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden">
          {STAGE_ACTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => handlePick(s)}
              className={`flex w-full items-center px-3 py-1.5 text-xs text-left hover:bg-gray-50 dark:hover:bg-gray-800 ${
                s === stage ? "font-semibold text-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-300"
              }`}
            >
              {stageLabel(s)}
            </button>
          ))}
        </div>
      )}

      {modals}
    </div>
  );
};

export default StageMenu;
export { STAGE_STYLES };
